import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
// import UploadIcon from "@mui/icons-material/Upload";
// import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast } from "react-toastify";
import {
  buildCityListParams,
  deleteCityMasterCity,
  getCities,
  getCityMasterById,
  getCityMasterStates,
  getCountriesForDropdown,
} from "../../api/superAdminApi";
import CityMasterFilters from "./CityMaster/CityMasterFilters";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import AddSingleCityModal from "./CityMaster/AddSingleCityModal";
// import CsvUploadWizard from "./CityMaster/CsvUploadWizard";
import GeoMasterActionCell from "./GeoMasterActionCell";
import GeoMasterDeleteDialog from "./GeoMasterDeleteDialog";

const ROWS_PER_PAGE_OPTIONS = [10, 20, 25, 50];
const TABLE_HEADERS = ["S.No", "City", "State", "Country", "Actions"];

export default function VMSuperAdminCityMaster() {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [countryId, setCountryId] = useState("all");
  const [stateId, setStateId] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [cityFormOpen, setCityFormOpen] = useState(false);
  const [addMode, setAddMode] = useState("single"); // "single" | "multiple"
  const [editingCity, setEditingCity] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // const [csvWizardOpen, setCsvWizardOpen] = useState(false);

  const filtersRef = useRef({ countryId, stateId, appliedSearch, page, rowsPerPage });
  filtersRef.current = { countryId, stateId, appliedSearch, page, rowsPerPage };

  const loadCountries = useCallback(async () => {
    setLoadingCountries(true);
    try {
      const res = await getCountriesForDropdown();
      const list = res.data?.data;
      setCountries(Array.isArray(list) ? list : []);
    } catch {
      setCountries([]);
      toast.error("Failed to load countries.");
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  const loadStates = useCallback(async (cid) => {
    if (!cid || cid === "all") {
      setStates([]);
      return;
    }
    setLoadingStates(true);
    try {
      const res = await getCityMasterStates(cid);
      const list = res.data?.data;
      setStates(Array.isArray(list) ? list : []);
    } catch {
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  }, []);

  const fetchList = useCallback(async (overrides = {}) => {
    const f = { ...filtersRef.current, ...overrides };
    setLoading(true);
    try {
      const params = buildCityListParams({
        page: f.page,
        pageSize: f.rowsPerPage,
        countryId: f.countryId,
        stateId: f.stateId,
        citySearch: f.appliedSearch,
      });
      const res = await getCities(params);
      const body = res.data;
      if (body?.status_code && body.status_code !== 200) {
        throw new Error(body.message || "Failed to load cities");
      }
      setRows(Array.isArray(body?.data) ? body.data : []);
      setTotalRecords(Number(body?.totalCount) || 0);
      if (typeof body?.page === "number") setPage(body.page);
    } catch (error) {
      setRows([]);
      setTotalRecords(0);
      toast.error(error?.response?.data?.message || error?.message || "City master API unavailable.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCountries();
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshGrid = useCallback(() => {
    fetchList();
  }, [fetchList]);

  const openAddCity = (mode = "single") => {
    setEditingCity(null);
    setAddMode(mode);
    setCityFormOpen(true);
  };

  const openEditCity = async (row) => {
    if (!row?.id) return;
    setEditLoading(true);
    try {
      const res = await getCityMasterById(row.id);
      const body = res.data;
      if (body?.status_code === 200 && body?.data) {
        setEditingCity(body.data);
        setAddMode("single");
        setCityFormOpen(true);
      } else {
        toast.error(body?.message || "Failed to load city.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load city.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteCity = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await deleteCityMasterCity(deleteRow.id);
      const body = res.data;
      if (body?.status_code === 200) {
        toast.success(body.message || "City deleted successfully");
        setDeleteRow(null);
        fetchList();
      } else {
        toast.error(body?.message || "Failed to delete city.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete city.");
    } finally {
      setDeleting(false);
    }
  };

  const handleCountryChange = (value) => {
    setCountryId(value);
    setStateId("all");
    setPage(0);
    loadStates(value);
    fetchList({ countryId: value, stateId: "all", page: 0 });
  };

  const handleStateChange = (value) => {
    setStateId(value);
    setPage(0);
    fetchList({ stateId: value, page: 0 });
  };

  const handleSearch = () => {
    const nextSearch = searchInput.trim();
    setAppliedSearch(nextSearch);
    setPage(0);
    setFiltersOpen(false);
    fetchList({ appliedSearch: nextSearch, page: 0 });
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    fetchList({ page: newPage });
  };

  const handleRowsPerPageChange = (e) => {
    const next = parseInt(e.target.value, 10);
    setRowsPerPage(next);
    setPage(0);
    fetchList({ rowsPerPage: next, page: 0 });
  };

  const from = totalRecords === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min((page + 1) * rowsPerPage, totalRecords);

  return (
    <Box>
      <SuperAdminPageHeader
        title="City Master"
        actions={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant={filtersOpen ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen((open) => !open)}
            >
              Filters
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(e) => setAddMenuAnchor(e.currentTarget)}
            >
              Add city
            </Button>
            <Menu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={() => setAddMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setAddMenuAnchor(null);
                  openAddCity("single");
                }}
              >
                <ListItemIcon>
                  <AddLocationIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Single</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAddMenuAnchor(null);
                  openAddCity("multiple");
                }}
              >
                <ListItemIcon>
                  <FormatListBulletedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Multiple</ListItemText>
              </MenuItem>
              {/* CSV / Excel upload — commented out for now
              <MenuItem
                onClick={() => {
                  setAddMenuAnchor(null);
                  setCsvWizardOpen(true);
                }}
              >
                <ListItemIcon>
                  <UploadFileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Upload CSV / Excel file</ListItemText>
              </MenuItem>
              */}
            </Menu>
          </Box>
        }
      />

      {filtersOpen ? (
        <CityMasterFilters
          countries={countries}
          states={states}
          countryId={countryId}
          stateId={stateId}
          citySearch={searchInput}
          onCountryChange={handleCountryChange}
          onStateChange={handleStateChange}
          onCitySearchChange={setSearchInput}
          onSearch={handleSearch}
          loadingCountries={loadingCountries}
          loadingStates={loadingStates}
          loading={loading}
        />
      ) : null}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Total cities (filtered): {loading ? "…" : totalRecords.toLocaleString()}
        {!loading && totalRecords > 0 ? ` · Showing ${from} to ${to}` : ""}
        {editLoading ? " · Loading city…" : ""}
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.main" }}>
              {TABLE_HEADERS.map((h) => (
                <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No cities found for the current criteria.
                </TableCell>
              </TableRow>
            ) : loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <TableRow key={row.id ?? `${page}-${idx}`} hover>
                  <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell>{row.city ?? "—"}</TableCell>
                  <TableCell>{row.state_name ?? "—"}</TableCell>
                  <TableCell>{row.country_name ?? "—"}</TableCell>
                  <TableCell>
                    <GeoMasterActionCell
                      onEdit={() => openEditCity(row)}
                      onDelete={() => setDeleteRow(row)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                count={totalRecords}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                disabled={loading}
                colSpan={TABLE_HEADERS.length}
                labelDisplayedRows={({ from: f, to: t, count }) =>
                  count === 0 ? "0 to 0 of 0" : `${f} to ${t} of ${count !== -1 ? count.toLocaleString() : `more than ${t}`}`
                }
                sx={{ border: 0 }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <AddSingleCityModal
        open={cityFormOpen}
        mode={addMode}
        initialData={editingCity}
        defaultCountryId={countryId}
        defaultStateId={stateId}
        onClose={() => {
          setCityFormOpen(false);
          setEditingCity(null);
          setAddMode("single");
        }}
        onSuccess={refreshGrid}
      />
      {/* CSV / Excel upload wizard — commented out for now
      <CsvUploadWizard
        open={csvWizardOpen}
        onClose={() => setCsvWizardOpen(false)}
        onSuccess={refreshGrid}
      />
      */}
      <GeoMasterDeleteDialog
        open={Boolean(deleteRow)}
        title="Delete city"
        message={`Delete "${deleteRow?.city || "this city"}"? It will be hidden from lists and venue dropdowns.`}
        loading={deleting}
        onClose={() => !deleting && setDeleteRow(null)}
        onConfirm={handleDeleteCity}
      />
    </Box>
  );
}
